/**
 * EvidenceRetentionNoticeProvider — shows the Art. 12(4) retention notice from
 * the app root, above every route guard.
 *
 * WHY AT THE ROOT: the account-deletion notice has to outlive the account. The
 * moment `DELETE /users/me` succeeds the session is gone, the settings page's
 * route guard redirects to login and unmounts it — a dialog owned by that page
 * would vanish before the citizen could read which company keeps what. Mounted
 * here, it survives the sign-out and the navigation, and the vault page uses
 * the same one so the citizen sees one notice, one way.
 *
 * `show()` with an EMPTY list is a no-op: when no company retains anything,
 * the citizen is shown nothing extra.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { EvidenceRetentionNotice } from '@tonnie/sdk';

import {
  EvidenceRetentionNoticeDialog,
  type EvidenceRetentionContext,
} from '../components/EvidenceRetentionNoticeDialog';

interface NoticeState {
  notices: EvidenceRetentionNotice[];
  context: EvidenceRetentionContext;
}

interface EvidenceRetentionNoticeApi {
  /** Show the notice for a delete response's `evidenceRetention`. Empty → nothing. */
  show: (notices: EvidenceRetentionNotice[], context: EvidenceRetentionContext) => void;
}

const EvidenceRetentionNoticeCtx = createContext<EvidenceRetentionNoticeApi | null>(null);

export function EvidenceRetentionNoticeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<NoticeState | null>(null);

  const show = useCallback<EvidenceRetentionNoticeApi['show']>((notices, context) => {
    if (notices.length === 0) return;
    setState({ notices, context });
  }, []);

  const api = useMemo(() => ({ show }), [show]);

  return (
    <EvidenceRetentionNoticeCtx.Provider value={api}>
      {children}
      {state ? (
        <EvidenceRetentionNoticeDialog
          open
          notices={state.notices}
          context={state.context}
          onClose={() => setState(null)}
        />
      ) : null}
    </EvidenceRetentionNoticeCtx.Provider>
  );
}

export function useEvidenceRetentionNotice(): EvidenceRetentionNoticeApi {
  const ctx = useContext(EvidenceRetentionNoticeCtx);
  if (!ctx) {
    throw new Error('useEvidenceRetentionNotice must be used within an EvidenceRetentionNoticeProvider');
  }
  return ctx;
}
